import { Link } from "react-router-dom";
import { SkillBadge } from "./SkillBadge";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export interface Job {
  id: string;
  title: string;
  company: {
    id: string;
    name: string;
  };
  skills: string[];
  tags?: string[];
  matchCount?: number;
  contactId?: string;
}

interface JobCardProps {
  job: Job;
  className?: string;
}

export const JobCard = ({ job, className }: JobCardProps) => {

  const { t } = useTranslation();

  // Limit skills and tags to 4 items
  const displaySkills = job.skills?.slice(0, 4) || [];
  const remainingSkills = job.skills?.length - 4 || 0;
  
  const displayTags = job.tags?.slice(0, 4) || [];
  const remainingTags = job.tags?.length - 4 || 0;

  return (
    <div 
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-xl",
        "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
        className
      )}
    >
      <div className="p-4 border-b border-border/50">
        <Link to={`/jobs/${job.id}`} className="hover:text-primary transition-colors">
          <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">{job.title}</h3>
        </Link>
        <Link 
          to={`/companies/${job.company.id}`}
          className="text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          {job.company.name}
        </Link>
      </div>
      
      <div className="flex-1 p-4">
        <div className="flex flex-col h-full">
          {displaySkills.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-1.5">
                {displaySkills.map((skill) => (
                  <SkillBadge key={skill} label={skill} />
                ))}
                {remainingSkills > 0 && (
                  <SkillBadge 
                    label={`+${remainingSkills} more`} 
                    className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  />
                )}
              </div>
            </div>
          )}
          
          {displayTags.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-1.5">
                {displayTags.map((tag) => (
                  <SkillBadge key={tag} label={tag} variant="primary" />
                ))}
                {remainingTags > 0 && (
                  <SkillBadge 
                    label={`+${remainingTags} more`} 
                    className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  />
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center mt-auto pt-2">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {job.matchCount !== undefined ? (
                <span><span className="font-medium">{job.matchCount}</span>{t("matches.potentialMatches")}</span>
              ) : (
                <span className="opacity-0">{t("matches.noMatchesFound")}</span>
              )}
            </div>
            
            <Link
              to={`/jobs/${job.id}`}
              className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {t("contacts.view")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
